import { memo } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import { Link } from "~/common/components/Link";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import { EMAIL_PATTERN } from "~/utils/constants";
import { MediasRegistrationBanner } from "~/features/registration/components/MediasRegistrationBanner/MediasRegistrationBanner";
import { Controller } from "react-hook-form";
import { MarketingConsentCheckbox } from "~/features/registration/components/MediasRegistrationBanner/MarketingConsentCheckbox";
import { TERMS_OF_USE_PAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { Box, Button, Card, Stack, TextField, styled } from "~/common/mui";
import { CustomerRegistrationBanner } from "~/features/registration/components/CustomerRegistrationBanner/CustomerRegistrationBanner";
import {
  ErrorMessage,
  HelperLabelErrorMessage,
  RegisterContactSalesSection,
  useRegisterForFree,
} from "~/features/registration/hooks/useRegisterForFree";
import PersonIcon from "@mui/icons-material/Person";

const RegisterForFreeDesktop = ({ showCustomerRegistration }: { showCustomerRegistration?: boolean }) => {
  const { t } = useTranslation();

  const {
    errors,
    watch,
    control,
    resetPopupOpen,
    loginPopupOpen,
    openLogin,
    closeLogin,
    closeReset,
    onResetClick,
    onSubmitHandler,
  } = useRegisterForFree();

  return (
    <RegisterFreeDesktopRoot className="scha__register-free register-desktop">
      <PageTitle title={t("page.registerFree.title")} />

      <Box sx={{ display: "flex", flexFlow: "column", gap: "32px" }}>
        <div className="scha__register-free__already">
          <span>{t("page.registerFree.already")} </span>
          <Link onClick={openLogin} className="scha__register-free__already__link">
            {t("page.registerFree.already-link")}
          </Link>
        </div>

        {showCustomerRegistration && <CustomerRegistrationBanner />}

        <div className="scha__register-free">
          <div className="scha__register-free__main-content">
            <div className="scha__register-free__main-content__left">
              <MediasRegistrationBanner />
            </div>
            <form className="scha__register-free__main-content__right" onSubmit={onSubmitHandler}>
              <Card
                component={Stack}
                gap={3}
                sx={{
                  borderRadius: 0,
                  padding: 2,
                }}
              >
                <h3 className="scha__register-free__main-content__right__title">{t("page.registerFree.title")}</h3>

                <Controller
                  name={"email"}
                  control={control}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      label={t("page.registerFree.label")}
                      error={!!error}
                      helperText={<HelperLabelErrorMessage error={error} />}
                      inputProps={{ "data-test-id": "register-for-free-email" }}
                      inputRef={ref}
                      {...inputProps}
                    />
                  )}
                  rules={{
                    required: { value: true, message: "error.invalidEmail" },
                    pattern: { value: EMAIL_PATTERN, message: "error.invalidEmail" },
                  }}
                />
                <Stack gap={2}>
                  <Controller
                    name={"generalTerms"}
                    control={control}
                    rules={{ required: { value: true, message: "error.tos" } }}
                    render={({ field: { value, onChange } }) => (
                      <CheckboxWithLabel
                        data-test-id={"register-for-free__general-terms"}
                        checked={value}
                        onCheck={onChange}
                        centerCheckbox={false}
                      >
                        {t("page.registerFree.terms-part1")}{" "}
                        <a
                          className="scha__register-free__main-content__right__checkbox-area__text__link"
                          href={TERMS_OF_USE_PAGE}
                        >
                          {t("page.registerFree.terms-link")}
                        </a>{" "}
                        {t("page.registerFree.terms-part2")}
                      </CheckboxWithLabel>
                    )}
                  />
                  <ErrorMessage error={errors.generalTerms} />

                  <Controller
                    name={"privacyPolicy"}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <MarketingConsentCheckbox
                        data-test-id={"register-for-free__privacy-policy"}
                        checked={value}
                        onCheck={onChange}
                        centerCheckbox={false}
                      />
                    )}
                  />

                  <div className="scha__register-free__main-content__right__mandatory">
                    {t("page.registerFree.mandatory")}
                  </div>
                </Stack>

                <Button
                  startIcon={<PersonIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
                  fullWidth={true}
                  data-test-id={"register-for-free__submit"}
                  onClick={onSubmitHandler}
                >
                  {t("page.registerFree.register")}
                </Button>
              </Card>
            </form>
            {loginPopupOpen && <LoginPopup initialEmail={""} onResetClick={onResetClick} close={closeLogin} />}
            {resetPopupOpen && <ResetPasswordPopup initialEmail={watch("email")} close={closeReset} />}
          </div>
          <RegisterContactSalesSection />
        </div>
      </Box>
    </RegisterFreeDesktopRoot>
  );
};

const RegisterFreeDesktopRoot = styled("div")(({ theme }) => ({
  "& .scha__register-free__already": {
    marginBottom: theme.spacing(1.25),
  },
  "& .scha__register-free__main-content": {
    display: "flex",
    width: "100%",
  },
  "& .scha__register-free__main-content__left": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.surface.main,
    flex: "auto",
    padding: `0 ${theme.spacing(2.5)}`,
  },
  "& .scha__register-free__main-content__right": {
    paddingLeft: theme.spacing(4),
    height: "100%",
    width: 480,
  },
  "& .scha__register-free__main-content__right__title": {
    marginTop: 0,
  },
  "& .scha__message": {
    minHeight: theme.spacing(2.5),
  },
}));

export default memo(RegisterForFreeDesktop);
